import {useState} from "react"
import Modal from "../../components/Modal"
import {useSelector} from "react-redux"
import ProductVariantPicker from "../../components/ProductVariantPicker"
import Icon from "../../components/Icon"
import IconButton from "../../components/IconButton"
import CheckBoxGroup from "../../components/CheckBoxGroup"

/**
 * Purpose: to specify which addons variants are on which parent product variants
 * e.g. ensure only the 12" olives are on the 12" pizza
 * 
 * NOTE: this links
 *  - ONE product variant
 *  - MANY addons and their variants
 */
const ProductAddonToVariantLinker = ({addonConfigs=[], onChange=()=>{}, parentVariantId="", parentVariantDetails="", parentProductName="", parentVariantsById={}}) => {
    //expand / hide the variant picker
    const [showVariantPicker, setShowVariantPicker] = useState(false)
    //open close the modal that displays the links
    const [modalOpen, setModalOpen] = useState(false)
    //build map of addon to variant links
    const payeeProductsById = useSelector(state  => state.payeeProducts.payeeProductsById || {})
    //only used for display in ProductVariantPicker as a combined variantsById
    const mixedAddonVariantsForVariantPicker = {}
    //controls CheckBoxGroup
    const addonVariantListsByConfigId = {}
    let numOfAddonsAvailable = 0
    let numOfAddonsSelected = 0
    //loop over all of the current addons. E.g both olive and cheese products
    addonConfigs.forEach(addonConfig => {
        //for each of the addon products e.g. olives / cheese
        const {allAddonsOnAllVariants, allowedAddonVariantsByProductVariantId={}, productId=""} = addonConfig
        //find the product the addon is for
        const addonProduct = payeeProductsById[productId]
        //get the variants of the addon product, e.g. different servings of olives
        const {variantsById:addonVariantsById={}} = addonProduct || {}
        Object.values(addonVariantsById)
            .forEach(addonVariant => {
                //for each of the servings of olives
                //link addon variant to parent variant
                const configVariantId = `${addonConfig.id}${addonVariant.id}` //allows us to have duplicate addonConfigs of one product 
                const addonConfigVariant = {
                    ...addonVariant,
                    variantId: addonVariant.id,
                    id: configVariantId,
                    label: addonVariant.variantDetails,
                    checked: false //default to false,
                }
                const allowedAddonMap = allowedAddonVariantsByProductVariantId[parentVariantId] || {}
                if (allAddonsOnAllVariants || allowedAddonMap[addonVariant.id]){
                    //if the addon is available on all of the product variants
                    //OR this addon is explicitly linked to the parent product variant
                    //then set it as checked
                    addonConfigVariant.checked = true
                    //add it to the display for the variant picker
                    mixedAddonVariantsForVariantPicker[configVariantId] = addonConfigVariant
                    numOfAddonsSelected++
                }
                //add to the group checkbox control 
                if (!addonVariantListsByConfigId[addonConfig.id]) addonVariantListsByConfigId[addonConfig.id] = []
                addonVariantListsByConfigId[addonConfig.id] = [
                    ...(addonVariantListsByConfigId[addonConfig.id] || []),
                    { ...addonConfigVariant }
                ]
                numOfAddonsAvailable++
            })
        
    })
    
    const handleSelectionSubmit = (newAddonSelection) => {
        /**
         * - rebuilds addonConfigs to reflect the new selection
         * - for each config, detects if all is selected 
         * - if all was previously selected implicitly, but the new selection for this current parent varaint breaks that,
         *   then it explicitly selects all addons for all other product variants 
         */
        const updatedAddonConfigs = addonConfigs.map(addonConfig => {
            const {allowedAddonVariantsByProductVariantId={}, allAddonsOnAllVariants:prevAllAvailable} = addonConfig
            const selectedAddonVariants = newAddonSelection[addonConfig.id] || [];
        
            // Build allowedAddonVariantsByProductVariantId
            let allowedMap = {...allowedAddonVariantsByProductVariantId};
            const selectedAddonVariantIds = selectedAddonVariants
                .filter(v => v.checked)
                .map(v => v.variantId);
            allowedMap[parentVariantId] = {};
            if (selectedAddonVariantIds.length > 0) {
                selectedAddonVariantIds.forEach(variantId => {
                allowedMap[parentVariantId][variantId] = true;
                });
            }
        
            let allAddonsOnAllVariants = false
            const addonProduct = payeeProductsById[addonConfig.productId]
            const {variantsById:addonVariantsById={}} = addonProduct || {}
            // Determine if all addons are available on all product variants after this selection
            //FIRST: check if all addons are selected for the current product variant
            const allAddonsOnCurrentProductVariant = selectedAddonVariants.length > 0 && selectedAddonVariants.every(v => v.checked);
            //NEXT
            if (allAddonsOnCurrentProductVariant) {
                //if all selected for current AND

                //One of the following is true
                //(a) previously, all addons were matched to all product variants, and the matching map is empty
                if (prevAllAvailable && Object.keys(allowedAddonVariantsByProductVariantId).length === 0){
                    allAddonsOnAllVariants = true
                } 
                else {
                    //OR (b)
                    //when we check all of the product variants they are either
                    //this one, where all addons are selected 
                    //OR they all have all of the addon variants selected
                    allAddonsOnAllVariants = Object.keys(parentVariantsById).every(productVariantId => {
                        //we already know that the current product variant has all addons
                        if (productVariantId === parentVariantId) return true
                        else {
                            // for all other product variants, 
                            //check that they have all addon variants allowed                           
                            const otherAllowed = allowedAddonVariantsByProductVariantId[productVariantId]
                            if (!otherAllowed || typeof otherAllowed !== "object") return false
                            return Object.keys(addonVariantsById).every(addonVariantId => otherAllowed[addonVariantId])
                        }      
                    })
                }
                //when the all addons flag is on, we don't specify addon-variant mappings
                if (allAddonsOnAllVariants) allowedMap = {}
            }
            else if(prevAllAvailable) {
                //if all addons were previously available on all product variants 
                //but this selection breaks that, since not all addons are selected
                //then loop through each of the other product variants 
                //and explicitly set all addons to true for the others
                Object.keys(parentVariantsById).forEach(productVariantId => {
                    if (productVariantId !== parentVariantId){
                        // for all other product variants, 
                        //check tbat they have all addon variants allowed                           
                        const otherAllowed = {}
                        Object.keys(addonVariantsById).forEach(addonVariantId => {
                            //explicitly flag every addon as true
                            otherAllowed[addonVariantId]=true
                        })
                        //add to allowed map
                        allowedMap[productVariantId] = otherAllowed
                    }      
                })
            }
            return {
                ...addonConfig,
                allowedAddonVariantsByProductVariantId: allowedMap,
                allAddonsOnAllVariants
            };
        });
      
        onChange(updatedAddonConfigs);
        setModalOpen(false)
        setShowVariantPicker(true)
    }
    return (
        <div>
            <div className="display-flex justify-content-space-between">
                <div>
                    <div    
                        className="display-flex align-items-center font-size-14px cursor-pointer hover-background-primary"
                        onClick={()=> setShowVariantPicker(!showVariantPicker)}    
                    >
                        <div><span className="font-weight-600">Add-ons:</span> {numOfAddonsSelected} of {numOfAddonsAvailable} added </div>
                        <span className="font-size-24px"><Icon icon={showVariantPicker ? "arrow-drop-up": "arrow-drop-down"}/></span>
                    </div>
                    {
                        showVariantPicker ?
                        <>
                        {
                            numOfAddonsSelected > 0 ?
                            <div style={{maxWidth: 200}}>
                                <ProductVariantPicker variantsById={mixedAddonVariantsForVariantPicker}/>
                            </div>
                            :
                            <div className="font-size-14px">No addons</div>
                        }
                        </>
                        :
                        null
                    }
                </div>
                <IconButton 
                    icon="edit" 
                    onClick={() => setModalOpen(true)}
                    buttonStyle={{
                        width: 30, height: 30,
                        backgroundColor: "var(--secondary)",
                        color: "white"
                    }}

                    label="Edit"
                />
            </div>
            {
                modalOpen ?
                <Modal
                    isOpen={modalOpen}
                    closeModal={() => setModalOpen(false)}
                    showCloseButton={true}
                    className="height-fit-content"
                    overlayClassName="flex-center"
                    closeOnOverlayClick={true}
                >
                    <AddonSelectionForm 
                        parentProductName={parentProductName}
                        parentVariantDetails={parentVariantDetails}
                        addonConfigs={addonConfigs}
                        addonVariantListsByConfigId={addonVariantListsByConfigId}
                        payeeProductsById={payeeProductsById}
                        onSubmit={handleSelectionSubmit}
                    />
                </Modal>
                :
                null
            }
        </div>
    )
}

const AddonSelectionForm = ({
    parentProductName="",
    parentVariantDetails="",
    addonConfigs=[],
    addonVariantListsByConfigId={},
    payeeProductsById={},
    onSubmit=()=>{}
}) => {
    const [selection, setSelection] = useState(addonVariantListsByConfigId)
    const handleChange = (addonVariantList, configId) => {
        setSelection({
            ...selection,
            [configId]: addonVariantList 
        })
    }
    const handleToggleAll = checked => {
        const newSelection = {}
        Object.keys(selection).forEach(configId => {
            const newAddonVariantList = selection[configId].map(addonVariant => ({
                                                                    ...addonVariant,
                                                                    checked
                                                                }))
            newSelection[configId] = newAddonVariantList
        })
        setSelection(newSelection)
    }
    const handleSubmit = () => {
        onSubmit(selection)
    }
    return (
        <div className="formContainer">
            <div className="formHeader">
                <div className="h2 text-align-center">Add-ons For {parentProductName} ({parentVariantDetails})</div>
                <div className="display-flex justify-content-flex-end text-align-right gap-2 margin-top-5px">
                    <button className="button success icon small font-size-16px" onClick={() => handleToggleAll(true)}><Icon icon="done"/> Select All</button>
                    <button className="button warning icon small font-size-16px" onClick={() => handleToggleAll(false)}><Icon icon="close"/> Deselect All</button>
                </div>
            </div>
            <div className="formBody">
                
                {
                    addonConfigs.map(addonConfig => {
                        const {productName="", groupLabel="", productId="", useProductNameAsLabel} = addonConfig
                        const {imageUrl=""} = payeeProductsById[productId]
                        return (
                            <CheckBoxGroup 
                                key={`${addonConfig.id}-checkbox-group`}
                                label={<div className="font-weight-600 display-flex">
                                            <div className="productImageContainer margin-right-5px" style={{height:32, width:32, minWidth:32 }}><img src={imageUrl}/></div>
                                            <div>{!useProductNameAsLabel && groupLabel ? groupLabel : productName}</div>
                                        </div>
                                    }
                                showParentCheckbox={true}
                                options={selection[addonConfig.id]}
                                onChange={({parentChecked, children}) => handleChange(children, addonConfig.id)}
                            />
                        )
                    })
                }
            </div>
            <div className="formFooter">
                <button className="button secondary width-100pct" onClick={handleSubmit}>Save Add-on Selection</button>
            </div>
        </div>
    )
}
export default ProductAddonToVariantLinker